import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo, aboutStats, aboutMilestones } from '@/mocks/data';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = (form.elements.namedItem('company_alt') as HTMLInputElement)?.value.trim();
    if (honeypot) {
      setFormStatus('success');
      setFormError('');
      form.reset();
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    const formData = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d96br7sut53jbls7km6g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // not JSON
      }

      const isSpam =
        (typeof parsed?.meta?.message === 'string' &&
          (parsed.meta.message as string).includes('spam')) ||
        responseText.includes('spam');

      if (isSpam) {
        setFormStatus('success');
        setFormError('');
        form.reset();
        return;
      }

      const serverMsg =
        (parsed as Record<string, unknown> | null)?.meta?.message ||
        typeof parsed === 'object' && parsed !== null && 'message' in parsed
          ? (parsed as Record<string, string>).message
          : null;

      if (response.ok && parsed && (parsed as Record<string, string>).code === 'OK') {
        setFormStatus('success');
        setFormError('');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    url: 'https://www.51chigua.com',
    description: siteInfo.description,
    email: siteInfo.email,
    telephone: siteInfo.phone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: siteInfo.address,
      addressCountry: 'CN',
    },
    foundingDate: siteInfo.established,
  };

  const webpageLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们 - ${siteInfo.name}`,
    description: siteInfo.description,
    url: 'https://www.51chigua.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: 'https://www.51chigua.com',
    },
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.51chigua.com' },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://www.51chigua.com/about' },
    ],
  };

  return (
    <>
      <Navbar />
      <main className="pt-16 md:pt-18">
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webpageLd) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />

        <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

        <section className="py-8 md:py-14 px-4 md:px-8 bg-background-50">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-2xl md:text-4xl font-heading font-extrabold text-foreground-900">
              关于<span className="text-accent-600">51吃瓜</span>
            </h1>
            <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              国内领先的娱乐资讯聚合平台，每日更新网红黑料、塌房现场及吃瓜爆料视频，为亿万网友提供最新最劲爆的吃瓜内容。
            </p>
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-8 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {aboutStats.map((stat) => (
                <div
                  key={stat.label}
                  className="bg-background-50 rounded-2xl p-5 md:p-6 text-center"
                >
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
                    <i className={`${stat.icon} text-lg text-accent-600`}></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-heading font-extrabold text-foreground-900">
                    {stat.value}
                  </div>
                  <div className="mt-1 text-xs md:text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-8 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8 md:mb-10">
              平台<span className="text-accent-600">发展历程</span>
            </h2>
            <div className="relative">
              <div className="absolute left-5 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 md:-translate-x-px"></div>
              <div className="space-y-8 md:space-y-12">
                {aboutMilestones.map((milestone, idx) => (
                  <div
                    key={milestone.year}
                    className={`relative flex flex-col md:flex-row gap-4 md:gap-8 ${
                      idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className="md:w-1/2 flex items-start gap-4 md:justify-end">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent-500 flex items-center justify-center z-10 relative">
                        <span className="text-xs font-bold text-primary-900">{milestone.year}</span>
                      </div>
                      <div
                        className={`md:hidden flex-1 bg-background-100 rounded-2xl p-4 ${
                          idx % 2 === 0 ? '' : 'md:ml-0'
                        }`}
                      >
                        <h3 className="font-bold text-foreground-900 text-sm">{milestone.title}</h3>
                        <p className="mt-1 text-xs text-foreground-600 leading-relaxed">{milestone.description}</p>
                      </div>
                    </div>
                    <div className="hidden md:block md:w-1/2">
                      <div className={`bg-background-100 rounded-2xl p-5 ${idx % 2 === 0 ? '' : ''}`}>
                        <h3 className="font-bold text-foreground-900">{milestone.title}</h3>
                        <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{milestone.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-8 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8 md:mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                品牌<span className="text-accent-600">介绍</span>
              </h2>
            </div>
            <div className="bg-background-50 rounded-2xl p-6 md:p-8 space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
              <p>
                <strong className="text-foreground-900">51吃瓜网站</strong>创立于{siteInfo.established}年，是国内最早专注于网红八卦与娱乐资讯聚合的平台之一。我们的愿景是成为中文互联网最值得信赖的吃瓜资讯平台，为用户提供快速、全面、真实的娱乐资讯服务。
              </p>
              <p>
                平台采用先进的信息聚合技术，7×24小时不间断追踪全网热点事件，确保用户能在第一时间获取最新吃瓜资讯。我们的编辑团队由资深娱乐记者和内容运营专家组成，具备丰富的行业经验和敏锐的新闻嗅觉。
              </p>
              <p>
                自成立以来，51吃瓜网站已累计报道超过12,800条独家爆料内容，日均服务580万+访问用户。我们与超过200家媒体机构建立了内容合作关系，在行业内建立了良好的口碑和影响力。
              </p>
              <p>
                作为负责任的媒体平台，51吃瓜网站始终坚持内容审核标准，对所有发布内容进行严格的事实核查，确保信息准确性和内容质量，致力于为用户创造健康、积极的吃瓜体验。
              </p>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-8 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8 md:mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                商务<span className="text-accent-600">合作</span>
              </h2>
              <p className="mt-2 text-sm text-foreground-600">
                欢迎品牌方、媒体机构及内容创作者与我们联系合作
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-heading font-bold text-foreground-900 mb-3">合作方式</h3>
                  <ul className="space-y-3 text-sm text-foreground-600">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-line text-accent-600 mt-0.5 flex-shrink-0"></i>
                      <span><strong className="text-foreground-900">品牌广告投放</strong> — 首页Banner、文章内嵌、分类页推荐位等多种广告形式</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-line text-accent-600 mt-0.5 flex-shrink-0"></i>
                      <span><strong className="text-foreground-900">内容合作</strong> — 联合报道、品牌专题策划、软文植入等深度内容合作</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-line text-accent-600 mt-0.5 flex-shrink-0"></i>
                      <span><strong className="text-foreground-900">媒体联盟</strong> — 加入51吃瓜媒体联盟，共享流量与内容资源</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-line text-accent-600 mt-0.5 flex-shrink-0"></i>
                      <span><strong className="text-foreground-900">内容投稿</strong> — 欢迎优质内容创作者投稿，享受高额稿酬与流量扶持</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-heading font-bold text-foreground-900 mb-3">联系方式</h3>
                  <div className="space-y-3 text-sm text-foreground-600">
                    <p className="flex items-center gap-2">
                      <i className="ri-mail-line text-accent-600 w-5 h-5 flex items-center justify-center"></i>
                      <span>商务邮箱：{siteInfo.email}</span>
                    </p>
                    <p className="flex items-center gap-2">
                      <i className="ri-phone-line text-accent-600 w-5 h-5 flex items-center justify-center"></i>
                      <span>合作热线：{siteInfo.phone}</span>
                    </p>
                    <p className="flex items-center gap-2">
                      <i className="ri-map-pin-line text-accent-600 w-5 h-5 flex items-center justify-center"></i>
                      <span>公司地址：{siteInfo.address}</span>
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 rounded-2xl p-5 md:p-6">
                <h3 className="text-lg font-heading font-bold text-foreground-900 mb-4">在线咨询</h3>
                <form
                  id="about-contact-form"
                  data-readdy-form=""
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div>
                    <label htmlFor="about-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="about-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-accent-400 transition-colors"
                      placeholder="请输入你的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="about-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-accent-400 transition-colors"
                      placeholder="请输入你的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-company" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      公司/品牌名称
                    </label>
                    <input
                      id="about-company"
                      name="company"
                      type="text"
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-accent-400 transition-colors"
                      placeholder="请输入公司或品牌名称"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-type" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      合作类型 <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="about-type"
                      name="cooperation_type"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-accent-400 transition-colors cursor-pointer"
                    >
                      <option value="">请选择合作类型</option>
                      <option value="广告投放">品牌广告投放</option>
                      <option value="内容合作">内容合作</option>
                      <option value="媒体联盟">媒体联盟</option>
                      <option value="内容投稿">内容投稿</option>
                      <option value="其他">其他合作</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="about-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      合作需求描述 <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="about-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-accent-400 transition-colors resize-none"
                      placeholder="请描述你的合作需求（500字以内）"
                    ></textarea>
                  </div>
                  <input
                    type="text"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="contact-hp"
                  />
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {formStatus === 'submitting' ? '提交中...' : '提交合作咨询'}
                  </button>

                  {formStatus === 'success' && (
                    <div className="text-center py-3 px-4 rounded-lg bg-accent-100">
                      <p className="text-sm font-medium text-accent-800">提交成功！我们将在3个工作日内与你联系</p>
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="text-center py-3 px-4 rounded-lg bg-red-50">
                      <p className="text-sm font-medium text-red-700">{formError}</p>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}